import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useCart } from "../../context/CartContext";
import { useAuth } from "../../context/AuthContext";
import { db } from "../../firebase/firebaseConfig";
import { collection, addDoc } from "firebase/firestore";
import { MapPin, User, Phone, CreditCard, ShoppingBag, ChevronRight } from "lucide-react";
import { getWhatsAppUrl } from "../../utils/Whatsapp";
const INITIAL = {
  firstName: "",
  lastName: "",
  phone: "",
  street: "",
  city: "",
  zip: "",
  payment: "cash",
};
const DELIVERY_CITY = "casablanca";
const OUTSIDE_CITY_FEE = 30;

// Normalise le texte (minuscules, sans accents, sans espaces superflus)
const normalize = (text = "") =>
  text.normalize("NFD").replace(/[\u0300-\u036f]/g, "").trim().toLowerCase();

const isOutsideCasa = (city) => normalize(city) !== DELIVERY_CITY;
export default function Checkout() {
  const { items, total, count, clearCart } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [form, setForm] = useState(INITIAL);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1);

  // Frais produits : le plus élevé parmi les produits payants
  const productShipping = items.reduce((max, item) => {
    if (item.freeShipping === false) {
      return Math.max(max, Number(item.shippingFee || 0));
    }
    return max;
  }, 0);

  // Frais ville : 30 MAD hors Casablanca
  const cityShipping = form.city.trim() && isOutsideCasa(form.city)
    ? OUTSIDE_CITY_FEE
    : 0;

  // Total livraison = frais produit + frais ville
  const shipping = productShipping + cityShipping;

  const grandTotal = total + shipping;

  // ── Champs ──────────────────────────────────────────────
  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
    setErrors(prev => ({ ...prev, [name]: "" }));
  };

  // ── Validation ──────────────────────────────────────────
  const validate = () => {
    const e = {};
    if (!form.firstName.trim()) e.firstName = "Prénom requis";
    if (!form.lastName.trim()) e.lastName = "Nom requis";
    if (!form.phone.trim()) e.phone = "Téléphone requis";
    if (!form.street.trim()) e.street = "Adresse requise";
    if (!form.city.trim()) e.city = "Ville requise";
    if (!form.zip.trim()) e.zip = "Code postal requis";
    return e;
  };

  // ── Étape 1 → 2 ─────────────────────────────────────────
  const handleNext = (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }
    setStep(2);
  };

  // ── Confirmer + sauvegarder Firestore ───────────────────
  const handleConfirm = async () => {
    setLoading(true);
    try {
      const orderItems = items.map((i) => ({
        productId: i.productId,
        name: i.name || "",
        variantId: i.variantId ?? null,
        variantLabel: i.variantLabel ?? null,
        price: Number(i.price || 0),
        image: i.image || "",
        category: i.category || "",
        quantity: i.quantity || 1,
        total: Number(i.price || 0) * (i.quantity || 1),
      }));

      const customer = {
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        phone: form.phone.trim(),
        street: form.street.trim(),
        city: form.city.trim(),
        zip: form.zip.trim(),
      };

      const ref = await addDoc(collection(db, "orders"), {
        userId: user?.uid ?? null,      // null = commande invité
        guest: !user,
        status: "pending",
        items: orderItems,
        shipping: customer,
        payment: form.payment,
        subtotal: total,
        frais: shipping,
        total: grandTotal,
        createdAt: new Date(),
      });

      clearCart();
      const order = {
        id: ref.id,
        items: orderItems,
        subtotal: total,
        shippingFee: shipping,
        total: grandTotal,
        payment: form.payment,
        shipping: customer,
      };

      window.open(getWhatsAppUrl(order), "_blank");

      clearCart();
      navigate("/thank-you", { state: { order } });
      navigate("/thank-you", {
        state: {
          order: {
            id: ref.id,
            items: orderItems,
            total: grandTotal,
            payment: form.payment,
            shipping: customer,
          },
        },
      });
    } catch (err) {
      console.error("Erreur commande :", err);
      alert("Erreur lors de la commande. Veuillez réessayer.");
    } finally {
      setLoading(false);
    }
  };

  // ── Panier vide ──────────────────────────────────────────
  if (items.length === 0) {
    return (
      <div className="container py-5 text-center">
        <ShoppingBag size={48} color="#6c63ff" style={{ opacity: 0.4 }} />
        <h4 className="fw-bold mt-3">Votre panier est vide</h4>
        <button onClick={() => navigate("/category/AllProducts")}
          className="btn mt-3 text-white rounded-3"
          style={{ background: "#6c63ff" }}>
          Continuer mes achats
        </button>
      </div>
    );
  }

  return (
    <div className="container py-5" style={{ maxWidth: 1000 }}>

      {/* ── Header + steps ── */}
      <div className="d-flex align-items-center gap-3 mb-4 flex-wrap">
        <h2 className="fw-bold mb-0">Finaliser la commande</h2>
        <div className="d-flex align-items-center gap-2 ms-auto">
          {["Livraison", "Confirmation"].map((s, i) => (
            <div key={i} className="d-flex align-items-center gap-2">
              <div className="rounded-circle d-flex align-items-center justify-content-center fw-bold"
                style={{
                  width: 28, height: 28, fontSize: 13,
                  background: step >= i + 1 ? "#6c63ff" : "#eee",
                  color: step >= i + 1 ? "#fff" : "#999",
                }}>
                {i + 1}
              </div>
              <span className="small"
                style={{ color: step >= i + 1 ? "#6c63ff" : "#999" }}>
                {s}
              </span>
              {i < 1 && <ChevronRight size={14} color="#ccc" />}
            </div>
          ))}
        </div>
      </div>

      <div className="row g-4">

        {/* ── Colonne gauche ── */}
        <div className="col-lg-7">

          {/* ÉTAPE 1 — Formulaire */}
          {step === 1 && (
            <form onSubmit={handleNext}
              className="card border-0 rounded-4 p-4"
              style={{ boxShadow: "0 4px 24px rgba(0,0,0,0.07)" }}>

              <h5 className="fw-bold mb-4 d-flex align-items-center gap-2">
                <MapPin size={18} color="#6c63ff" /> Adresse de livraison
              </h5>

              <div className="row mb-3">
                <div className="col-6">
                  <label className="form-label fw-medium small">
                    <User size={12} className="me-1" /> Prénom *
                  </label>
                  <input name="firstName" value={form.firstName}
                    onChange={handleChange}
                    className={`form-control rounded-3 ${errors.firstName ? "is-invalid" : ""}`}
                    placeholder="Prénom" />
                  {errors.firstName && <div className="invalid-feedback">{errors.firstName}</div>}
                </div>
                <div className="col-6">
                  <label className="form-label fw-medium small">Nom *</label>
                  <input name="lastName" value={form.lastName}
                    onChange={handleChange}
                    className={`form-control rounded-3 ${errors.lastName ? "is-invalid" : ""}`}
                    placeholder="Nom" />
                  {errors.lastName && <div className="invalid-feedback">{errors.lastName}</div>}
                </div>
              </div>

              <div className="mb-3">
                <label className="form-label fw-medium small">
                  <Phone size={12} className="me-1" /> Téléphone *
                </label>
                <input name="phone" value={form.phone}
                  onChange={handleChange}
                  className={`form-control rounded-3 ${errors.phone ? "is-invalid" : ""}`}
                  placeholder="00000000000" />
                {errors.phone && <div className="invalid-feedback">{errors.phone}</div>}
              </div>

              <div className="mb-3">
                <label className="form-label fw-medium small">Adresse *</label>
                <input name="street" value={form.street}
                  onChange={handleChange}
                  className={`form-control rounded-3 ${errors.street ? "is-invalid" : ""}`}
                  placeholder="Adresse" />
                {errors.street && <div className="invalid-feedback">{errors.street}</div>}
              </div>

              <div className="row mb-4">
                <div className="col-7">
                  <label className="form-label fw-medium small">Ville *</label>
                  <input name="city" value={form.city}
                    onChange={handleChange}
                    className={`form-control rounded-3 ${errors.city ? "is-invalid" : ""}`}
                    placeholder="Ville" />
                  {errors.city && <div className="invalid-feedback">{errors.city}</div>}
                </div>
                <div className="col-5">
                  <label className="form-label fw-medium small">Code postal *</label>
                  <input name="zip" value={form.zip}
                    onChange={handleChange}
                    className={`form-control rounded-3 ${errors.zip ? "is-invalid" : ""}`}
                    placeholder="Code postal" />
                  {errors.zip && <div className="invalid-feedback">{errors.zip}</div>}
                </div>
              </div>

              {/* Paiement */}
              <h5 className="fw-bold mb-3 d-flex align-items-center gap-2">
                <CreditCard size={18} color="#6c63ff" /> Mode de paiement
              </h5>
              <div className="d-flex flex-column gap-2 mb-4">
                {[
                  { value: "cash", label: "Paiement à la livraison", emoji: "💵" },
                ].map(opt => (
                  <label key={opt.value}
                    className="d-flex align-items-center gap-3 p-3 rounded-3"
                    style={{
                      border: `1px solid ${form.payment === opt.value ? "#6c63ff" : "#eee"}`,
                      background: form.payment === opt.value ? "rgba(108,99,255,0.05)" : "#fff",
                      cursor: "pointer", transition: "all .2s",
                    }}>
                    <input type="radio" name="payment" value={opt.value}
                      checked={form.payment === opt.value}
                      onChange={handleChange}
                      style={{ accentColor: "#6c63ff" }} />
                    <span style={{ fontSize: 18 }}>{opt.emoji}</span>
                    <span className="fw-medium small">{opt.label}</span>
                  </label>
                ))}
              </div>

              <button type="submit"
                className="btn w-100 fw-bold text-white rounded-3 py-2 d-flex align-items-center justify-content-center gap-2"
                style={{ background: "#6c63ff" }}>
                Continuer <ChevronRight size={16} />
              </button>
            </form>
          )}

          {/* ÉTAPE 2 — Confirmation */}
          {step === 2 && (
            <div className="card border-0 rounded-4 p-4"
              style={{ boxShadow: "0 4px 24px rgba(0,0,0,0.07)" }}>

              <h5 className="fw-bold mb-4">Récapitulatif de livraison</h5>

              <div className="rounded-3 p-3 mb-4"
                style={{ background: "#f8f9ff", border: "1px solid rgba(108,99,255,0.15)" }}>
                <p className="mb-1 fw-medium">{form.firstName} {form.lastName}</p>
                <p className="mb-1 text-muted small">{form.phone}</p>
                <p className="mb-0 text-muted small">
                  {form.street}, {form.city} {form.zip}
                </p>
              </div>

              {/* Paiement choisi */}
              <div className="rounded-3 p-3 mb-4"
                style={{ background: "#f8f9ff", border: "1px solid rgba(108,99,255,0.15)" }}>
                <p className="mb-0 fw-medium small">
                  Mode de paiement :
                  <span style={{ color: "#6c63ff", marginLeft: 8 }}>
                    {{ cash: "💵 Livraison", card: "💳 Carte", transfer: "🏦 Virement" }[form.payment]}
                  </span>
                </p>
              </div>

              <div className="d-flex gap-2">
                <button onClick={() => setStep(1)}
                  className="btn btn-outline-secondary rounded-3 flex-grow-1">
                  ← Modifier
                </button>
                <button onClick={handleConfirm} disabled={loading}
                  className="btn fw-bold text-white rounded-3 flex-grow-1"
                  style={{ background: "#6c63ff" }}>
                  {loading
                    ? <><span className="spinner-border spinner-border-sm me-2" />Traitement...</>
                    : "✅ Confirmer la commande"
                  }
                </button>
              </div>
            </div>
          )}
        </div>

        {/* ── Résumé droite ── */}
        <div className="col-lg-5">
          <div className="card border-0 rounded-4 p-4"
            style={{ boxShadow: "0 4px 24px rgba(0,0,0,0.07)", position: "sticky", top: 80 }}>

            <h5 className="fw-bold mb-3">
              Résumé ({count} article{count > 1 ? "s" : ""})
            </h5>

            <div className="d-flex flex-column gap-2 mb-3">
              {items.map(item => (
                <div key={item.productId} className="d-flex align-items-center gap-2">
                  <img src={item.image || "https://via.placeholder.com/44"}
                    alt={item.name} className="rounded-2"
                    style={{ width: 44, height: 44, objectFit: "cover", flexShrink: 0 }}
                    onError={e => e.target.src = "https://via.placeholder.com/44"} />
                  <div className="flex-grow-1 overflow-hidden">
                    <p className="mb-0 small fw-medium text-truncate">{item.name}</p>
                    <p className="mb-0 small text-muted">x{item.quantity}</p>
                  </div>
                  <span className="small fw-bold" style={{ color: "#6c63ff" }}>
                    {((item.price || 0) * (item.quantity || 1)).toFixed(2)} MAD
                  </span>
                </div>
              ))}
            </div>

            <hr />

            <div className="d-flex justify-content-between small mb-1">
              <span className="text-muted">Sous-total</span>
              <span>{total.toFixed(2)} MAD</span>
            </div>
            <div className="d-flex justify-content-between small mb-3">
              <span className="text-muted">Livraison</span>
              <span style={{ color: shipping === 0 ? "#16a34a" : "inherit" }}>
                {shipping === 0 ? "Gratuite 🎉" : `${shipping.toLocaleString("fr-MA")} MAD`}
              </span>
            </div>

            {cityShipping > 0 && (
              <p className="small text-muted mb-3">
                Livraison hors Casablanca : {OUTSIDE_CITY_FEE} MAD
              </p>
            )}
            <div className="d-flex justify-content-between fw-bold" style={{ fontSize: 18 }}>
              <span>Total</span>
              <span style={{ color: "#6c63ff" }}>{grandTotal.toFixed(2)} MAD</span>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}